/**
 * UI 状态管理
 * 管理全局加载状态、错误提示等 UI 相关状态
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  // 加载状态
  isLoading: false,         // 全局加载状态
  isThinking: false,        // 异步任务等待状态
  isMarkLoading: false,     // 批改结果加载状态
  pageLoading: false,       // 页面级加载状态
  
  // 错误状态
  errorMessage: '',
  
  // 提示消息
  toastMessage: '',
  toastType: 'info',        // 'info' | 'success' | 'warning' | 'error'
};

export const useUIStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========== 加载状态操作 ==========

      /**
       * 设置全局加载状态
       * @param {boolean} loading
       */
      setIsLoading: (loading) => set({ isLoading: loading }, false, 'ui/setIsLoading'),

      /**
       * 设置异步任务等待状态
       * @param {boolean} thinking
       */
      setIsThinking: (thinking) => set({ isThinking: thinking }, false, 'ui/setIsThinking'),

      /**
       * 设置批改结果加载状态
       * @param {boolean} loading
       */
      setIsMarkLoading: (loading) => set({ isMarkLoading: loading }, false, 'ui/setIsMarkLoading'),

      /**
       * 设置页面级加载状态
       * @param {boolean} loading
       */
      setPageLoading: (loading) => set({ pageLoading: loading }, false, 'ui/setPageLoading'),

      /**
       * 开始加载（设置 isLoading 为 true）
       */
      startLoading: () => set({ isLoading: true }, false, 'ui/startLoading'),

      /**
       * 结束加载（重置所有加载状态）
       */
      stopLoading: () => set({
        isLoading: false,
        isThinking: false,
        isMarkLoading: false
      }, false, 'ui/stopLoading'),

      // ========== 错误处理 ==========

      /**
       * 设置错误消息
       * @param {string} message
       */
      setErrorMessage: (message) => set({ errorMessage: message }, false, 'ui/setErrorMessage'),

      /**
       * 清除错误消息
       */
      clearError: () => set({ errorMessage: '' }, false, 'ui/clearError'),

      // ========== 提示消息 ==========

      /**
       * 显示提示消息
       * @param {string} message
       * @param {'info' | 'success' | 'warning' | 'error'} type
       */
      showToast: (message, type = 'info') => set({
        toastMessage: message,
        toastType: type
      }, false, 'ui/showToast'),

      /**
       * 清除提示消息
       */
      clearToast: () => set({ toastMessage: '', toastType: 'info' }, false, 'ui/clearToast'),

      /**
       * 显示成功提示
       * @param {string} message
       */
      showSuccess: (message) => {
        set({ toastMessage: message, toastType: 'success' }, false, 'ui/showSuccess');
      },

      /**
       * 显示错误提示
       * @param {string} message
       */
      showError: (message) => {
        set({ toastMessage: message, toastType: 'error' }, false, 'ui/showError');
      },

      // ========== 重置 ==========

      /**
       * 重置 UI 状态
       */
      resetUI: () => set(initialState, false, 'ui/reset'),
    }),
    { name: 'UIStore' }
  )
);
